import React, { useState, useEffect, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { MdCancel } from 'react-icons/md'
import FormControl from 'react-bootstrap/FormControl'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import { PhoneInput } from 'react-international-phone'

import { red } from '../../../styles/themeColors'
import { ValidationModal } from '../../../components'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { GuestShort } from '../../../types'
import {
  addFreeGuest,
  editFreeGuest,
  freeGuestsWasUpdated,
  removeFreeGuest,
} from '../../../features/bookingSlice'
import { useValidatePhone } from '../../../components/SigninSignup/components/SigninSignupFunctions'

import SelectGuestModal, { IAddGuestPayload } from './SelectGuestModal'

interface IguestInfo {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | undefined
  isPlusOne: boolean
  bookingAddonId?: string
  main?: boolean
}

export const FreeGuests: React.FC<{
  basic: number
  guestShort: GuestShort
  showPlusOne: boolean
  myGroup?: GuestShort[]
  onValidationChange?: (isValid: boolean) => void
}> = ({ basic, guestShort, showPlusOne, myGroup = [], onValidationChange }) => {
  const dispatch = useAppDispatch()
  const {
    availableAddons: { extraFreeGuestAddon },
    freeGuests = [],
    freeGuestsUpdated,
    renderHelper,
  } = useAppSelector(state => state.booking)

  const [showValidationModal, setShowValidationModal] = useState(false)
  const [showSelectGuestModal, setShowSelectGuestModal] = useState(false)
  const [btnHelper, setBtnHelper] = useState(() =>
    Array.from({ length: basic }, () => true)
  )
  const [guestList, setGuestListInfo] = useState<IguestInfo[]>([])
  const [fgCount, setFgCount] = useState(() => freeGuests.length)

  // Phone validation states
  const [phones, setPhones] = useState<Record<string, string | undefined>>({})
  const [phoneErrors, setPhoneErrors] = useState<Record<string, string>>({})
  const { validatePhone } = useValidatePhone()

  const allPhonesValid = useCallback(() => {
    if (Object.keys(phones).length === 0) return true
    const hasPhoneErrors = Object.values(phoneErrors).some(
      error => error !== ''
    )
    if (hasPhoneErrors) return false

    return Object.entries(phones).every(([key, value]) => {
      // Skip undefined phones (they're valid)
      if (value === undefined) return true
      return validatePhone({
        phone: value,
        setPhoneError: error =>
          setPhoneErrors(prev => ({
            ...prev,
            [key]: error as string,
          })),
        required: false,
      })
    })
  }, [phones, phoneErrors, validatePhone])

  useEffect(() => {
    if (renderHelper <= 0) return

    const length = freeGuests?.length
    if (length <= 0 && extraFreeGuestAddon) {
      // If there are no free guests, we block the payment button
      dispatch(freeGuestsWasUpdated(true))
    }

    if (length > 0 && extraFreeGuestAddon) {
      // If there are free guests, we add them to the current guest list
      setGuestListInfo(
        freeGuests.map(el => ({
          id: el.id,
          firstName: el.guest.firstName,
          lastName: el.guest.lastName,
          email: el.guest.email || '',
          phone: el.guest.phone || undefined,
          isPlusOne: el.guest.isPlusOne,
          bookingAddonId: el.bookingAddOnId,
          main: el.mainGuest,
        }))
      )
      // Update phones state
      const newPhones: Record<string, string | undefined> = {}
      freeGuests.forEach((guest, index) => {
        newPhones[`phone-${index}`] = guest.guest.phone || undefined
      })
      setPhones(newPhones)

      dispatch(freeGuestsWasUpdated(false))
    }
  }, [dispatch, extraFreeGuestAddon, freeGuests, renderHelper])

  useEffect(() => {
    if (guestList.length > 0) {
      const initialPhones: Record<string, string | undefined> = {}
      guestList.forEach((guest, index) => {
        initialPhones[`phone-${index}`] = guest.phone || undefined
      })
      setPhones(initialPhones)
    }
  }, [guestList])

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(allPhonesValid())
    }
  }, [phones, phoneErrors, onValidationChange, allPhonesValid])

  const handleSubmit = (guestInfo: IguestInfo, idx: number) => {
    if (!extraFreeGuestAddon) return

    const id = guestInfo?.id ?? uuidv4()
    const form = document.querySelector(`#form-${idx}`) as HTMLFormElement
    const addingNewGuest = !guestInfo?.id
    const isMainGuest = form.email.value === guestShort.email

    // Get the phone value from our state instead of the form
    const phoneValue = phones[`phone-${idx}`]
    // Validate phone (empty is valid)
    const phoneIsValid = validatePhone({
      phone: phoneValue,
      setPhoneError: error =>
        setPhoneErrors(prev => ({
          ...prev,
          [`phone-${idx}`]: error as string,
        })),
      required: false,
    })

    // If phone is invalid, don't proceed with saving
    if (!phoneIsValid) {
      return
    }

    const guest: IguestInfo = {
      id: id || uuidv4(),
      firstName: form.firstName.value,
      lastName: form.lastName.value,
      email: form.email.value,
      phone: phoneValue,
      isPlusOne: guestInfo.isPlusOne || false,
      bookingAddonId: extraFreeGuestAddon.id,
      main: isMainGuest,
    }

    dispatch(
      addingNewGuest
        ? addFreeGuest({
            id,
            guest: {
              firstName: guest.firstName,
              lastName: guest.lastName,
              email: guest.email,
              phone: guest.phone || '',
              isPlusOne: guest.isPlusOne,
            },
            bookingAddOnId: guestInfo && guestInfo.bookingAddonId,
            addon: extraFreeGuestAddon,
            mainGuest: guest.main,
          })
        : editFreeGuest({
            id: guestInfo.id,
            guest: {
              firstName: guest.firstName,
              lastName: guest.lastName,
              email: guest.email,
              phone: guest.phone || '',
              isPlusOne: guest.isPlusOne,
            },
            bookingAddOnId: guestInfo && guestInfo.bookingAddonId,
            addon: extraFreeGuestAddon,
            mainGuest: guest.main,
          })
    )

    setGuestListInfo(prev =>
      addingNewGuest
        ? [...prev, { ...guest, id }]
        : prev.map(el =>
            el.id === guestInfo.id
              ? { ...guest, id: guestInfo.id, main: isMainGuest }
              : el
          )
    )
    setBtnHelper(prev => prev.map((el, i) => (i === idx ? true : el)))
    dispatch(freeGuestsWasUpdated(false))
  }

  const handleDelete = (guestInfo: IguestInfo) => {
    dispatch(
      removeFreeGuest({
        guestId: guestInfo.id,
        bookingAddOnId: guestInfo.bookingAddonId,
      })
    )
    setGuestListInfo(prev => prev.filter(el => el.id !== guestInfo.id))
    setFgCount(prev => prev - 1)
  }

  const dataHasChanged = (idx: number) => {
    if (btnHelper[idx]) {
      setBtnHelper(prev => prev.map((el, i) => (i === idx ? false : el)))
    }

    if (!freeGuestsUpdated) {
      dispatch(freeGuestsWasUpdated(true))
    }
  }

  const handleAddGuest = (guest: IAddGuestPayload) => {
    if (!extraFreeGuestAddon) return

    const id = guest.guest?.id ?? uuidv4()
    const isMainGuest = guest.email === guestShort.email

    dispatch(
      addFreeGuest({
        id,
        guest: {
          ...guest,
          // If the guest is the main guest, we dont allow them to be a +1
          isPlusOne: isMainGuest ? false : guest.isPlusOne,
        },
        addon: extraFreeGuestAddon,
        mainGuest: isMainGuest,
      })
    )

    setGuestListInfo(prev => [
      ...prev,
      {
        ...guest,
        id,
        main: isMainGuest,
      } as IguestInfo,
    ])
    setFgCount(prev => prev + 1)
    dispatch(freeGuestsWasUpdated(false))
  }

  const handlePhoneChange = (value: string, idx: number) => {
    if (value?.trim() === '+1') return // Ignore the default value

    const phoneValue = value.trim() === '' ? undefined : value
    setPhones(prev => ({ ...prev, [`phone-${idx}`]: phoneValue }))
    validatePhone({
      phone: value,
      setPhoneError: error =>
        setPhoneErrors(prev => ({
          ...prev,
          [`phone-${idx}`]: error as string,
        })),
      required: false,
    })
    if (btnHelper[idx]) {
      setBtnHelper(prev => prev.map((el, i) => (i === idx ? false : el)))
    }
  }

  const forms = Array.from({ length: fgCount }, (_, idx) => {
    const guest = guestList[idx]
    const isNewGuest = !guest
    return (
      <form
        onSubmit={ev => {
          ev.preventDefault()
          handleSubmit(guest, idx)
        }}
        key={`${idx}-${guest?.id}`}
        id={`form-${idx}`}
      >
        <Table
          borderless
          size="sm"
          data-cy="free-guests"
          className="mb-1 free-guests-form-table"
        >
          {idx === 0 && (
            <thead>
              <tr>
                <th>
                  First Name
                  <span className="text-danger"> *</span>
                </th>
                <th>
                  Last Name<span className="text-danger"> *</span>
                </th>
                <th>Email</th>
                <th>Phone</th>
                <th>{/** save button */}</th>
                <th>{/** delete button */}</th>
              </tr>
            </thead>
          )}
          <tbody>
            <tr>
              <td>
                <p className="d-block d-md-none mb-1 fwsb">
                  First Name <span className="text-danger">*</span>
                </p>
                <FormControl
                  onChange={() => dataHasChanged(idx)}
                  id="firstName"
                  defaultValue={guest?.firstName}
                  required
                />
              </td>
              <td>
                <p className="d-block d-md-none mb-1 fwsb">
                  Last Name <span className="text-danger">*</span>
                </p>
                <FormControl
                  onChange={() => dataHasChanged(idx)}
                  id="lastName"
                  defaultValue={guest?.lastName}
                  required
                />
              </td>
              <td>
                <p className="d-block d-md-none mb-1 fwsb">Email</p>
                <FormControl
                  onChange={() => dataHasChanged(idx)}
                  id={`email-${idx}`}
                  name="email"
                  defaultValue={guest?.email}
                  type="email"
                />
              </td>
              <td>
                <p className="d-block d-md-none mb-1 fwsb">Phone</p>
                <PhoneInput
                  placeholder="Phone number"
                  defaultCountry="us"
                  value={phones[`phone-${idx}`]}
                  onChange={value => handlePhoneChange(value, idx)}
                  className="international-phone-small"
                  inputClassName="international-phone-input-small"
                  countrySelectorStyleProps={{
                    buttonClassName: 'international-phone-button-small',
                  }}
                />
                {phoneErrors[`phone-${idx}`] && (
                  <span className="fz-12 text-danger d-block">
                    {phoneErrors[`phone-${idx}`]}
                  </span>
                )}
              </td>
              <td className="align-middle">
                <Button
                  size="sm"
                  type="submit"
                  className="rounded-pill m-0 px-4 px-md-2"
                  disabled={btnHelper[idx]}
                >
                  save
                </Button>
              </td>
              <td className="align-middle" style={{ width: '30px' }}>
                {!isNewGuest && (
                  <MdCancel
                    size={20}
                    color={red}
                    title="Delete"
                    className="cursor"
                    style={{ margin: '0' }}
                    onClick={() => handleDelete(guest)}
                  />
                )}
              </td>
            </tr>
            {!btnHelper[idx] && (
              <tr>
                <td className="text-danger fz-12 text-left pt-0" colSpan={6}>
                  * unsaved changes
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </form>
    )
  })

  return (
    <>
      <ValidationModal
        text="You have reached the maximum number of +1.<br />"
        textClass="fz-18 pt-2"
        show={showValidationModal}
        onHide={() => setShowValidationModal(false)}
      />
      {forms}

      {fgCount < basic && (
        <div className="w-100">
          <Button
            size="sm"
            variant="outline-primary"
            className="rounded-pill m-0 px-3 px-md-5 m-1"
            onClick={() => setShowSelectGuestModal(true)}
          >
            Add Guest
          </Button>
        </div>
      )}

      {fgCount <= 0 && (
        <p className="text-danger fz-12 text-left pt-0">
          * You need to add at least one guest to proceed
        </p>
      )}

      <SelectGuestModal
        show={showSelectGuestModal}
        onHide={() => setShowSelectGuestModal(false)}
        onAddGuest={handleAddGuest}
        myGroup={myGroup}
        showPlusOne={showPlusOne}
        currentGuest={guestShort}
      />
    </>
  )
}
